/**
 * Společné utility pro výpočet date ranges s podporou výrobního dne.
 */

import moment from 'moment-timezone';
import { toProductionDayStart, toProductionDayEnd, getLastCompletedProductionDayEnd, getDayStartMinutes } from './production-day.utils';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

// Ujistíme se, že moment.js používá týden začínající pondělím (ISO standard)
moment.updateLocale('cs', {
    week: {
        dow: 1, // Pondělí je první den v týdnu (0 = neděle, 1 = pondělí)
        doy: 4, // První týden roku je týden obsahující 4. leden (ISO standard)
    },
});

export type PeriodPreset =
    | 'previousDay'
    | 'last7d'
    | 'last14d'
    | 'last30d'
    | 'last3m'
    | 'thisWeek'
    | 'lastWeek'
    | 'thisMonth'
    | 'lastMonth'
    | 'thisQuarter'
    | 'lastQuarter'
    | 'thisYear'
    | 'lastYear'
    | 'custom';

export interface DateRangeParams {
    periodPreset: PeriodPreset;
    customFrom?: string | null;
    customTo?: string | null;
    granularity?: 'day' | 'week' | 'month';
}

export interface DateRange {
    from: string;
    to: string;
}

/**
 * Vypočítá date range na základě preset hodnoty nebo custom rozsahu.
 * Všechny rozsahy jsou automaticky převedeny na výrobní dny.
 * 
 * Proces výpočtu:
 * 1. Nejprve vypočítáme kalendářní rozsah podle preset hodnoty
 * 2. Pak převedeme oba kraje na výrobní den (začátek a konec)
 * 
 * Příklad: při dayStart = "06:00:00" a preset "last7d" dnes (7.10.2025 15:00)
 *   - Kalendářní: 30.9.2025 15:00 - 7.10.2025 15:00
 *   - Výrobní: 30.9.2025 06:00 - 8.10.2025 06:00
 * 
 * @param params Parametry s preset nebo custom rozsahem
 * @returns Objekt s from a to jako ISO stringy (začátek a konec výrobního dne)
 */
export function getDateRange(params: DateRangeParams): DateRange {
    // Konec období je vždy začátek dnešního výrobního dne (= konec včerejšího výrobního dne)
    const lastCompletedDayEnd = getLastCompletedProductionDayEnd();
    let to = lastCompletedDayEnd;
    let from = moment(to).add(-7, 'days').toISOString();
    const p = params.periodPreset;

    // Předchozí den - celý včerejší výrobní den
    if (p === 'previousDay') {
        from = moment(lastCompletedDayEnd).subtract(1, 'day').toISOString();
        to = lastCompletedDayEnd;
    }
    // Posledních X dní - vždy od (dnes - X dní) do konce včerejšího výrobního dne
    if (p === 'last7d') {
        from = moment(lastCompletedDayEnd).subtract(7, 'days').toISOString();
        to = lastCompletedDayEnd;
    }
    if (p === 'last14d') {
        from = moment(lastCompletedDayEnd).subtract(14, 'days').toISOString();
        to = lastCompletedDayEnd;
    }
    if (p === 'last30d') {
        from = moment(lastCompletedDayEnd).subtract(30, 'days').toISOString();
        to = lastCompletedDayEnd;
    }
    if (p === 'last3m') {
        from = moment(lastCompletedDayEnd).subtract(3, 'months').toISOString();
        to = lastCompletedDayEnd;
    }
    
    // Tento týden - od pondělí do konce včerejšího výrobního dne
    if (p === 'thisWeek') {
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00), aby toProductionDayStart() neposunul datum o den zpět
        from = moment(lastCompletedDayEnd).startOf('isoWeek').hour(12).minute(0).second(0).millisecond(0).toISOString();
        to = lastCompletedDayEnd;
    }
    
    // Minulý týden - od pondělí do neděle předchozího týdne
    if (p === 'lastWeek') {
        const lastMonday = moment(lastCompletedDayEnd).startOf('isoWeek').subtract(7, 'days');
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = lastMonday.hour(12).minute(0).second(0).millisecond(0).toISOString();
        // Konec týdne = neděle 23:59:59.999
        to = lastMonday.clone().endOf('isoWeek').toISOString();
    }
    
    // Tento měsíc - od 1. dne měsíce do konce včerejšího výrobního dne
    if (p === 'thisMonth') {
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = moment(lastCompletedDayEnd).startOf('month').hour(12).minute(0).second(0).millisecond(0).toISOString();
        to = lastCompletedDayEnd;
    }
    
    // Minulý měsíc - od 1. dne do posledního dne předchozího měsíce
    if (p === 'lastMonth') {
        const lastMonthStart = moment(lastCompletedDayEnd).subtract(1, 'month').startOf('month');
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = lastMonthStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        // Konec měsíce = poslední den 23:59:59.999
        to = lastMonthStart.clone().endOf('month').toISOString();
    }
    
    // Toto čtvrtletí - od 1. dne čtvrtletí do konce včerejšího výrobního dne
    if (p === 'thisQuarter') {
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = moment(lastCompletedDayEnd).startOf('quarter').hour(12).minute(0).second(0).millisecond(0).toISOString();
        to = lastCompletedDayEnd;
    }
    
    // Minulé čtvrtletí - od 1. dne do posledního dne předchozího čtvrtletí
    if (p === 'lastQuarter') {
        const lastQuarterStart = moment(lastCompletedDayEnd).subtract(1, 'quarter').startOf('quarter');
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = lastQuarterStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        // Konec čtvrtletí = poslední den 23:59:59.999
        to = lastQuarterStart.clone().endOf('quarter').toISOString();
    }
    
    // Tento rok - od 1.1. do konce včerejšího výrobního dne
    if (p === 'thisYear') {
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = moment(lastCompletedDayEnd).startOf('year').hour(12).minute(0).second(0).millisecond(0).toISOString();
        to = lastCompletedDayEnd;
    }
    
    // Minulý rok - od 1.1. do 31.12. předchozího roku
    if (p === 'lastYear') {
        const lastYearStart = moment(lastCompletedDayEnd).subtract(1, 'year').startOf('year');
        // DŮLEŽITÉ: Nastavíme from na poledne (12:00)
        from = lastYearStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        // Konec roku = 31.12. 23:59:59.999
        to = lastYearStart.clone().endOf('year').toISOString();
    }
    
    // Custom rozsah od uživatele
    if (p === 'custom' && params.customFrom && params.customTo) {
        from = params.customFrom;
        to = params.customTo;
    }

    // DŮLEŽITÉ: Zajistit, že 'to' není v budoucnu (později než začátek dnešního výrobního dne)
    const maxAllowedEnd = lastCompletedDayEnd;
    if (moment(to).isAfter(maxAllowedEnd)) {
        to = maxAllowedEnd;
    }

    // KLÍČOVÝ KROK: Převedeme kalendářní rozsah na výrobní dny
    // Pro CUSTOM období: pouze přidáme dayStart čas, NESMÍME měnit datum!
    // Pro ostatní období: standardní převod pomocí toProductionDayStart/End
    if (p === 'custom') {
        // Pro custom: nastavíme jen čas podle dayStart, datum zůstává stejné
        const dayStartMinutes = getDayStartMinutes();
        const dayStartHours = Math.floor(dayStartMinutes / 60);
        const dayStartMins = dayStartMinutes % 60;
        
        // from: nastavit čas na dayStart, datum zachovat
        const fromMoment = moment(from);
        from = fromMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
        
        // to: nastavit čas na dayStart, datum zachovat
        const toMoment = moment(to);
        to = toMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
    } else {
        // Standardní převod pro preset období
        // - from → začátek výrobního dne (např. 7.10. 00:00 → 7.10. 06:00)
        // - to → konec výrobního dne (např. 7.10. 23:59 → 8.10. 06:00)
        from = toProductionDayStart(from);
        to = toProductionDayEnd(to);
    }
    
    // Finální kontrola - zajistit, že to není v budoucnu po převodu na výrobní den
    if (moment(to).isAfter(maxAllowedEnd)) {
        to = maxAllowedEnd;
    }

    return { from, to };
}

/**
 * Vypočítá předchozí období stejné délky.
 * 
 * Používá se pro porovnání "aktuální vs. předchozí období" (např. tento týden vs. minulý týden).
 * 
 * Logika:
 * 1. Získáme aktuální rozsah (už v výrobních dnech)
 * 2. Spočítáme délku období v minutách
 * 3. Posuneme oba kraje zpět o tuto délku
 * 4. Opět převedeme na výrobní dny (kvůli edge cases)
 * 
 * Příklad: aktuální = 1.10. 06:00 - 8.10. 06:00 (7 dní)
 *   - Předchozí = 24.9. 06:00 - 1.10. 06:00 (také 7 dní)
 * 
 * @param params Parametry období (stejné jako pro getDateRange)
 * @returns Objekt s from a to jako ISO stringy pro předchozí období
 */
export function getPreviousDateRange(params: DateRangeParams): DateRange {
    console.log('[getPreviousDateRange] START', {
        periodPreset: params.periodPreset,
        granularity: params.granularity,
    });
    
    const { from, to } = getDateRange(params);
    console.log('[getPreviousDateRange] current period from getDateRange', {
        from,
        to,
        fromFormatted: moment.tz(from, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
        toFormatted: moment.tz(to, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
    });
    
    const p = params.periodPreset;
    const lastCompletedDayEnd = getLastCompletedProductionDayEnd();
    console.log('[getPreviousDateRange] lastCompletedDayEnd', lastCompletedDayEnd);
    
    let prevFrom: string;
    let prevTo: string;
    
    // Předchozí den - předchozí období je den před tím (předpředchozí den)
    if (p === 'previousDay') {
        prevFrom = moment(lastCompletedDayEnd).subtract(2, 'days').toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(1, 'day').toISOString();
        console.log('[getPreviousDateRange] previousDay', { prevFrom, prevTo });
    }
    // Posledních X dní/měsíců - předchozí období je stejně dlouhé, jen posunuté zpět
    else if (p === 'last7d') {
        prevFrom = moment(lastCompletedDayEnd).subtract(14, 'days').toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(7, 'days').toISOString();
        console.log('[getPreviousDateRange] last7d', { prevFrom, prevTo });
    }
    else if (p === 'last14d') {
        prevFrom = moment(lastCompletedDayEnd).subtract(28, 'days').toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(14, 'days').toISOString();
        console.log('[getPreviousDateRange] last14d', { prevFrom, prevTo });
    }
    else if (p === 'last30d') {
        prevFrom = moment(lastCompletedDayEnd).subtract(60, 'days').toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(30, 'days').toISOString();
        console.log('[getPreviousDateRange] last30d', { prevFrom, prevTo });
    }
    else if (p === 'last3m') {
        prevFrom = moment(lastCompletedDayEnd).subtract(6, 'months').toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(3, 'months').toISOString();
        console.log('[getPreviousDateRange] last3m', { prevFrom, prevTo });
    }
    // Tento týden - předchozí je minulý týden od pondělí do (dnes - 7 dní)
    else if (p === 'thisWeek') {
        prevFrom = moment(lastCompletedDayEnd).startOf('isoWeek').subtract(7, 'days').hour(12).minute(0).second(0).millisecond(0).toISOString();
        prevTo = moment(lastCompletedDayEnd).subtract(7, 'days').toISOString();
        console.log('[getPreviousDateRange] thisWeek', { 
            prevFrom, 
            prevTo,
            prevFromFormatted: moment.tz(prevFrom, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
            prevToFormatted: moment.tz(prevTo, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
        });
    }
    // Minulý týden - předchozí je o týden dříve (pondělí - neděle)
    else if (p === 'lastWeek') {
        const lastMonday = moment(lastCompletedDayEnd).startOf('isoWeek').subtract(14, 'days');
        prevFrom = lastMonday.hour(12).minute(0).second(0).millisecond(0).toISOString();
        prevTo = lastMonday.clone().endOf('isoWeek').toISOString();
        console.log('[getPreviousDateRange] lastWeek', { prevFrom, prevTo });
    }
    // Tento měsíc - předchozí je stejný počet dnů v předchozím měsíci
    else if (p === 'thisMonth') {
        // DŮLEŽITÉ: Začít od 1. dne PŘEDCHOZÍHO měsíce (ne jen odečíst měsíc od from)
        const prevMonthStart = moment(lastCompletedDayEnd).subtract(1, 'month').startOf('month');
        prevFrom = prevMonthStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        
        // Nastavit prevTo: odečíst 1 měsíc od aktuálního 'to' (zachová den i čas)
        let prevToMoment = moment(to).subtract(1, 'month');
        
        // Ošetřit případ, kdy předchozí měsíc má méně dní (např. 31.3. → 28.2.)
        const currentDayOfMonth = moment(to).date();
        if (prevToMoment.date() !== currentDayOfMonth) {
            // Nastavit na poslední den předchozího měsíce (ale zachovat čas z 'to')
            const timeFromTo = {
                hour: moment(to).hour(),
                minute: moment(to).minute(),
                second: moment(to).second(),
                millisecond: moment(to).millisecond(),
            };
            prevToMoment = prevMonthStart.clone().endOf('month')
                .hour(timeFromTo.hour)
                .minute(timeFromTo.minute)
                .second(timeFromTo.second)
                .millisecond(timeFromTo.millisecond);
        }
        
        prevTo = prevToMoment.toISOString();
        console.log('[getPreviousDateRange] thisMonth', { 
            prevFrom, 
            prevTo, 
            currentDayOfMonth,
            prevDayOfMonth: moment.tz(prevTo, DISPLAY_TIMEZONE).date(),
            currentTo: moment.tz(to, DISPLAY_TIMEZONE).format('DD.MM.YYYY HH:mm'),
            prevToFormatted: prevToMoment.format('DD.MM.YYYY HH:mm'),
        });
    }
    // Minulý měsíc - předchozí je o měsíc dříve
    else if (p === 'lastMonth') {
        // DŮLEŽITÉ: Použít lastCompletedDayEnd jako referenci, ne 'from' které může být zaokrouhlené na týden
        const prevMonthStart = moment(lastCompletedDayEnd).subtract(2, 'months').startOf('month');
        prevFrom = prevMonthStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        prevTo = prevMonthStart.clone().endOf('month').toISOString();
        console.log('[getPreviousDateRange] lastMonth', { prevFrom, prevTo });
    }
    // Toto čtvrtletí - předchozí je stejný počet dnů v předchozím čtvrtletí
    else if (p === 'thisQuarter') {
        // DŮLEŽITÉ: Začít od 1. dne PŘEDCHOZÍHO čtvrtletí (ne jen odečíst čtvrtletí od from)
        const prevQuarterStart = moment(lastCompletedDayEnd).subtract(1, 'quarter').startOf('quarter');
        prevFrom = prevQuarterStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        
        // Nastavit prevTo: odečíst 3 měsíce od aktuálního 'to' (zachová den i čas)
        const prevToMoment = moment(to).subtract(3, 'months');
        prevTo = prevToMoment.toISOString();
        
        console.log('[getPreviousDateRange] thisQuarter', { 
            prevFrom, 
            prevTo,
            currentTo: moment.tz(to, DISPLAY_TIMEZONE).format('DD.MM.YYYY HH:mm'),
            prevToFormatted: prevToMoment.format('DD.MM.YYYY HH:mm'),
        });
    }
    // Minulé čtvrtletí - předchozí je o čtvrtletí dříve
    else if (p === 'lastQuarter') {
        // DŮLEŽITÉ: Použít lastCompletedDayEnd jako referenci, ne 'from' které může být zaokrouhlené na týden
        const prevQuarterStart = moment(lastCompletedDayEnd).subtract(2, 'quarters').startOf('quarter');
        prevFrom = prevQuarterStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        prevTo = prevQuarterStart.clone().endOf('quarter').toISOString();
        console.log('[getPreviousDateRange] lastQuarter', { prevFrom, prevTo });
    }
    // Tento rok - předchozí je stejný počet dnů v předchozím roce
    else if (p === 'thisYear') {
        // DŮLEŽITÉ: Začít od 1. ledna PŘEDCHOZÍHO roku (ne jen odečíst rok od from)
        const prevYearStart = moment(lastCompletedDayEnd).subtract(1, 'year').startOf('year');
        prevFrom = prevYearStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        
        // Nastavit prevTo: odečíst 1 rok od aktuálního 'to' (zachová den i čas)
        const prevToMoment = moment(to).subtract(1, 'year');
        prevTo = prevToMoment.toISOString();
        
        console.log('[getPreviousDateRange] thisYear', { 
            prevFrom, 
            prevTo,
            currentTo: moment.tz(to, DISPLAY_TIMEZONE).format('DD.MM.YYYY HH:mm'),
            prevToFormatted: prevToMoment.format('DD.MM.YYYY HH:mm'),
        });
    }
    // Minulý rok - předchozí je o rok dříve
    else if (p === 'lastYear') {
        // DŮLEŽITÉ: Použít lastCompletedDayEnd jako referenci, ne 'from' které může být zaokrouhlené na měsíc
        const prevYearStart = moment(lastCompletedDayEnd).subtract(2, 'years').startOf('year');
        prevFrom = prevYearStart.hour(12).minute(0).second(0).millisecond(0).toISOString();
        prevTo = prevYearStart.clone().endOf('year').toISOString();
        console.log('[getPreviousDateRange] lastYear', { prevFrom, prevTo });
    }
    // Custom - předchozí je posunuté o stejnou délku zpět
    else {
        const spanDays = moment(to).diff(moment(from), 'days');
        prevTo = from;
        prevFrom = moment(from).subtract(spanDays, 'days').toISOString();
        console.log('[getPreviousDateRange] custom', { prevFrom, prevTo, spanDays });
    }

    console.log('[getPreviousDateRange] RESULT (PŘED převedením na výrobní dny)', {
        prevFrom,
        prevTo,
        prevFromFormatted: moment.tz(prevFrom, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
        prevToFormatted: moment.tz(prevTo, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
    });

    // DŮLEŽITÉ: Převést na výrobní dny stejně jako u hlavního období
    // Pro CUSTOM období: pouze přidáme dayStart čas, NESMÍME měnit datum!
    // Pro ostatní období: standardní převod pomocí toProductionDayStart/End
    if (p === 'custom') {
        // Pro custom: nastavíme jen čas podle dayStart, datum zůstává stejné
        const dayStartMinutes = getDayStartMinutes();
        const dayStartHours = Math.floor(dayStartMinutes / 60);
        const dayStartMins = dayStartMinutes % 60;
        
        // prevFrom: nastavit čas na dayStart, datum zachovat
        const prevFromMoment = moment(prevFrom);
        prevFrom = prevFromMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
        
        // prevTo: nastavit čas na dayStart, datum zachovat
        const prevToMoment = moment(prevTo);
        prevTo = prevToMoment.hour(dayStartHours).minute(dayStartMins).second(0).millisecond(0).toISOString();
    } else {
        // Standardní převod pro preset období
        prevFrom = toProductionDayStart(prevFrom);
        prevTo = toProductionDayEnd(prevTo);
    }

    console.log('[getPreviousDateRange] RESULT (PO převedení na výrobní dny)', {
        prevFrom,
        prevTo,
        prevFromFormatted: moment.tz(prevFrom, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
        prevToFormatted: moment.tz(prevTo, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm'),
    });

    return {
        from: prevFrom,
        to: prevTo,
    };
}

/**
 * Vypočítá default granularitu podle typu období a délky.
 * 
 * @param params Parametry období
 * @returns Default granularita ('day', 'week' nebo 'month')
 */
/**
 * Vrátí povolené granularity pro dané období.
 */
export function getAllowedGranularities(params: DateRangeParams): Array<'day' | 'week' | 'month'> {
    const p = params.periodPreset;
    
    // Předchozí den, Posledních 7 dní, 14 dní, Tento týden, Minulý týden - pouze DNY
    if (p === 'previousDay' || p === 'last7d' || p === 'last14d' || p === 'thisWeek' || p === 'lastWeek') {
        return ['day'];
    }
    
    // Posledních 30 dní, Tento měsíc, Minulý měsíc - DNY + TÝDNY
    if (p === 'last30d' || p === 'thisMonth' || p === 'lastMonth') {
        return ['day', 'week'];
    }
    
    // Posledních 3 měsíce, Toto čtvrtletí, Minulé čtvrtletí, Tento rok, Minulý rok - TÝDNY + MĚSÍCE
    if (p === 'last3m' || p === 'thisQuarter' || p === 'lastQuarter' || p === 'thisYear' || p === 'lastYear') {
        return ['week', 'month'];
    }
    
    // Vlastní období - závisí na délce
    if (p === 'custom' && params.customFrom && params.customTo) {
        const days = moment(params.customTo).diff(moment(params.customFrom), 'days');
        if (days <= 14) return ['day'];
        if (days <= 31) return ['day', 'week'];
        return ['week', 'month'];
    }
    
    // Default - všechny granularity
    return ['day', 'week', 'month'];
}

export function getDefaultGranularity(params: DateRangeParams): 'day' | 'week' | 'month' {
    // Vrátíme první povolenou granularitu
    const allowed = getAllowedGranularities(params);
    return allowed[0];
}
